import React, { useState, useRef, useEffect, useMemo } from "react";
import { DOCUMENT_SUGGESTIONS } from "../../constants/constants";

const DocumentNameAutocomplete = ({ 
  value, 
  onChange, 
  selectedSellerTypes = [],
  placeholder = "Enter Document Name",
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value || "");
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const suggestions = useMemo(() => {
    const allSuggestions = new Set([...DOCUMENT_SUGGESTIONS.common]);

    selectedSellerTypes.forEach(type => {
      const typeSuggestions = DOCUMENT_SUGGESTIONS[type];
      if (typeSuggestions) {
        typeSuggestions.forEach(s => allSuggestions.add(s));
      }
    });

    const suggestionArray = Array.from(allSuggestions);

    if (!inputValue.trim()) {
      return suggestionArray;
    }

    const searchTerm = inputValue.toLowerCase();
    return suggestionArray.filter(s => 
      s.toLowerCase().includes(searchTerm)
    ).sort((a, b) => {
      const aStartsWith = a.toLowerCase().startsWith(searchTerm);
      const bStartsWith = b.toLowerCase().startsWith(searchTerm);
      if (aStartsWith && !bStartsWith) return -1;
      if (!aStartsWith && bStartsWith) return 1;
      return a.localeCompare(b);
    });
  }, [inputValue, selectedSellerTypes]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);
    if (!isOpen && newValue.length > 0) {
      setIsOpen(true);
    }
  };

  const handleFocus = () => {
    setIsOpen(true);
  };

  const handleSelect = (suggestion) => {
    setInputValue(suggestion);
    onChange(suggestion);
    setIsOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={`w-full p-2 border border-grey-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${className}`}
        autoComplete="off"
      />
      
      {isOpen && suggestions.length > 0 && (
        <div 
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-grey-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          <div className="px-3 py-2 text-xs font-medium text-grey-500 bg-grey-50 border-b border-grey-200">
            Suggested Documents
          </div>
          {suggestions.map((suggestion, index) => {
            const isExactMatch = suggestion.toLowerCase() === inputValue.toLowerCase();
            return (
              <button
                key={index}
                type="button"
                onClick={() => handleSelect(suggestion)}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-green-50 transition-colors ${
                  isExactMatch ? 'bg-green-50 text-green-700 font-medium' : 'text-grey-700'
                }`}
              >
                {highlightMatch(suggestion, inputValue)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const highlightMatch = (text, query) => {
  if (!query.trim()) return text;
  
  const index = text.toLowerCase().indexOf(query.toLowerCase());
  if (index === -1) return text;
  
  const before = text.slice(0, index);
  const match = text.slice(index, index + query.length);
  const after = text.slice(index + query.length);
  
  return (
    <>
      {before}
      <span className="font-semibold text-green-600">{match}</span>
      {after}
    </>
  );
};

export default DocumentNameAutocomplete;
